// The SCROLL-SNAP transition engine: a static-slot pager where every member is a fixed 100%-wide slot and the
// scroller's scrollLeft IS the current page. `scroll-snap-stop: always` gives one page per swipe.
// A programmatic Link runs on the COMPOSITOR instead (runComposited) — scrollLeft is not animatable, so every
// frame of a scroll-driven glide would be a main-thread write.
// Byte-identical with the sibling repo; an engine sync must carry lib/snap-scroller.ts across too.
// No mid-gesture interaction: the drag IS the browser's scroll, so it owns the touch stream — see README (9C)
// for the four windows and why only the landed-but-uncommitted one is reachable.

import { type ReactNode, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Outlet, useRouter, useRouterState } from '@tanstack/react-router'
import { cn } from '@/lib/utils'
import { RoutePreview, useTransitionPreviewFrom } from './route-preview'
import { captureSnapshot } from './snapshot'
import { navVisualPath, transitionProgress } from '../core/nav-visual-path'
import { indexOfMember, matchesFrom, type RoutePath, type TransitionOutletProps } from './surface.config'
import { resolveScrollCommit } from './scroll-pager-machine'
import { observeSnapScroller, type SnapScrollerHandle } from '@/lib/snap-scroller'
import { SPRING_TRAJECTORY } from '../core/spring'
import { createMidpointHaptic } from '../core/haptics'
import { rtlog, rtInstallDump } from '../core/debug'
import { prefersReducedMotion } from '../core/motion'

// Only reached when a scrollLeft write draws no scroll event at all, so no settle ever releases the claim.
const PROGRAMMATIC_WATCHDOG_MS = 1000
// Hand-driven because scrollTo({ behavior: 'smooth' }) runs for a UA-chosen duration (~450ms in Chromium,
// growing with distance) and no scroll or scroll-snap API exposes a knob for it.
const GLIDE_MS = 280
// The app's shared spring, time-scaled into GLIDE_MS rather than retuned: a spring's duration is emergent from
// its stiffness and damping, so changing it to hit 280ms would change the nav stack's and the sheet's feel too.
const GLIDE_SAMPLES = SPRING_TRAJECTORY
// Reveal anyway if a committed navigation never resolves in-group (a redirect out of the roster, an error).
const PARK_WATCHDOG_MS = 2000

// A real element rather than .rt-scroll-slot::before: a pseudo-element's opacity can only be driven through a
// custom property on the slot, and those inherit — invalidating the whole route pane's computed style per frame.
function SeamStrip() {
  return <i data-rt-seam="" className="rt-seam" aria-hidden />
}

// Cached off the scroll path so the seam writer never reads layout on a frame.
// A module WeakMap keyed by the scroller, not a ref: writing ref.current inside a callback makes the React
// Compiler treat everything reachable through it as frozen and drops the whole component from optimization.
const slotGeom = new WeakMap<HTMLElement, { el: HTMLElement; seam: HTMLElement | null; left: number }[]>()

export function ScrollTransitionOutlet(props: TransitionOutletProps) {
  const router = useRouter()
  const scope = props.previewFrom ?? '/'
  // Inside a live preview we ARE the pane content: a plain outlet, no scroller, no nested pager.
  const inPreview = useTransitionPreviewFrom() !== null
  // The MATCHES, not a location: during a navigation `location` flips first and `resolvedLocation` last, and only
  // the matches put the relocation in the SAME commit as the content change.
  const pathname = useRouterState({
    select: (s) => s.matches[s.matches.length - 1]?.pathname ?? s.resolvedLocation?.pathname ?? s.location.pathname,
  })
  // Only the park's watchdog reads it — a slow commit and a navigation that never arrives must be told apart.
  const routerPending = useRouterState({ select: (s) => s.status === 'pending' })
  // Mirrored for callbacks that outlive the render they were created in — see releaseChrome.
  const pathnameRef = useRef(pathname)
  // A LAYOUT effect, not render: a ref write during render bails the React Compiler out of the whole component.
  useLayoutEffect(() => {
    pathnameRef.current = pathname
  })
  const group = props.group
  const currentIndex = indexOfMember(group, pathname)

  const rootRef = useRef<HTMLDivElement | null>(null)
  const scrollerRef = useRef<HTMLDivElement | null>(null)
  const liveRef = useRef<HTMLDivElement | null>(null)
  const widthRef = useRef(1)
  // lib/snap-scroller.ts owns the frame coalescing, the settle detection and the held-finger rules; this surface
  // only supplies what to do on a frame and what a landing means.
  const gestureRef = useRef<SnapScrollerHandle | null>(null)
  // True only while WE own scrollLeft, so our own scrolls are not read as a user swipe. Released by the settle
  // that scroll produces, or by a real gesture taking over.
  // The timer is a WATCHDOG only, for a write the browser answers with no scroll event; a stuck hold cannot cause
  // a wrong navigation, because a commit also needs userIntentRef.
  const programmaticRef = useRef(false)
  const programmaticRelease = useRef(0)
  // Set by a real gesture (touch/pointer/wheel); a commit only fires for a scroll the user actually started.
  const userIntentRef = useRef(false)
  // Ticks on the frame the finger takes the page past halfway — the same instant the chrome flips, never on commit.
  // A useState initializer, not useMemo: it closes over latch state and useMemo is a hint React may discard.
  const [midHaptic] = useState(createMidpointHaptic)
  // Distinguishes the INITIAL placement (instant) from a later Link nav (glide), and lets a swipe-commit skip it.
  const lastPositioned = useRef(-1)
  // A far jump teleports next to its destination, so the slots it physically crosses are NOT the journey: while
  // this is set the chrome interpolates end-to-end and nothing in between is ever active.
  const glideRef = useRef<{ from: string; to: string; start: number; end: number } | null>(null)
  /** rAF handle of the hand-driven glide (see runGlide). Non-zero = a glide we own is mid-flight. */
  const glideAnimRef = useRef(0)
  // Non-null = the surface is mid-slide on the compositor, and scrollLeft is ALREADY parked on the destination.
  const compositeRef = useRef<{ layer: HTMLElement; anims: Animation[]; chromeRaf: number } | null>(null)
  // A glide whose slide finished while its destination is still loading; released when the router commits.
  const pendingChromeRef = useRef<string | null>(null)
  const pendingChromeTimer = useRef(0)

  const [mounted, setMounted] = useState(false)
  // oxlint-disable-next-line react/set-state-in-effect -- driven by scroll position on an external scroller, not derivable during render
  useEffect(() => setMounted(true), [])
  // Starts TRUE so SSR and the first client render agree; the effect then tracks the live media queries.
  const [suppressed, setSuppressed] = useState(true)
  const suppressedRef = useRef(true)
  const reducedMotionRef = useRef(false)
  useEffect(() => {
    if (inPreview) return
    const mqFine = window.matchMedia('(pointer: fine)')
    const mqReduce = window.matchMedia('(prefers-reduced-motion: reduce)')
    const update = () => {
      reducedMotionRef.current = mqReduce.matches
      suppressedRef.current = mqFine.matches || mqReduce.matches
      setSuppressed(suppressedRef.current)
    }
    update()
    mqFine.addEventListener('change', update)
    mqReduce.addEventListener('change', update)
    return () => {
      mqFine.removeEventListener('change', update)
      mqReduce.removeEventListener('change', update)
    }
  }, [inPreview])
  // The full pager exists only past SSR, on a touch device, with more than one member to page between.
  const expanded = mounted && !suppressed && group.length > 1 && !inPreview

  // The member a swipe committed to, whose live <Outlet/> is PARKED off-stage until the router resolves onto it.
  const [parkedFor, setParkedFor] = useState<string | null>(null)
  // Set the ref too: a swipe commit navigates in the SAME tick, so the state has not flushed when onBeforeLoad fires.
  const parkedForRef = useRef<string | null>(null)
  // Layout effect, not render — see the first mirror above, and README Appendix C.
  useLayoutEffect(() => {
    parkedForRef.current = parkedFor
  })
  const parkWatchdog = useRef(0)

  // Reset to the current member's neighbours on settle, so only a handful of shadow routers are ever mounted.
  const [activated, setActivated] = useState<ReadonlySet<string>>(() => new Set())
  // PUSHED IN, because the engine is created once and therefore cannot capture them.
  const input = useRef({ group, scope })
  useLayoutEffect(() => {
    input.current = { group, scope }
  })

  // Created once, so no effect can tear down a router subscription merely because a closure was re-created.
  // A useState initializer, not useMemo (a hint React may discard) and not a ref write in render (a Compiler
  // bail-out). Everything here closes over refs, module helpers, stable setters and `input` — never a state VALUE,
  // which is what makes create-once safe: capture something reactive and it freezes at the first render.
  const [engine] = useState(() => {
    const activate = (path: string) => {
      setActivated((prev) => {
        if (prev.has(path)) return prev
        const next = new Set(prev)
        next.add(path)
        return next
      })
    }

    // THREE call sites, because no one of them covers every navigation: the positioning effect returns early on a
    // link glide, so the glide's own finish trims too, and so does the deferred chrome release. Miss one and the
    // whole roster's shadow routers stay mounted for the session.
    const trimPreviews = (around: string) => {
      // Never before the destination has COMMITTED: until then its preview IS the pane on screen.
      if (!matchesFrom(pathnameRef.current, around)) return
      // A finger, a glide still in flight, or a park mid-resolve are all still using the slots they mounted.
      if (gestureRef.current?.held() || glideAnimRef.current || compositeRef.current || parkedForRef.current) return
      const index = indexOfMember(input.current.group, around)
      if (index < 0) return
      const neighbours = new Set<string>()
      const prev = input.current.group[index - 1]
      const next = input.current.group[index + 1]
      if (prev) neighbours.add(prev)
      if (next) neighbours.add(next)
      // Same set → same reference, so a trim that changes nothing doesn't cost a render.
      setActivated((current) =>
        current.size === neighbours.size && [...neighbours].every((m) => current.has(m)) ? current : neighbours,
      )
    }

    // Held for a whole glide rather than a guessed number of milliseconds, which keeps onScroll from mounting a
    // preview into every slot a 280ms glide merely travels through.
    const holdProgrammatic = () => {
      programmaticRef.current = true
      if (programmaticRelease.current) clearTimeout(programmaticRelease.current)
      programmaticRelease.current = window.setTimeout(releaseProgrammatic, PROGRAMMATIC_WATCHDOG_MS)
    }

    /** Hand the scroll back — called by the settle it guarded, by a finger taking over, and by the watchdog. */
    const releaseProgrammatic = () => {
      programmaticRef.current = false
      if (programmaticRelease.current) {
        clearTimeout(programmaticRelease.current)
        programmaticRelease.current = 0
      }
    }

    // Idempotent — the finish handler, a superseding click, a finger and teardown all call it. scrollLeft is
    // deliberately NOT touched: it has sat on the destination since the glide started.
    const endComposite = () => {
      const c = compositeRef.current
      if (!c) return
      compositeRef.current = null
      cancelAnimationFrame(c.chromeRaf)
      for (const a of c.anims) a.cancel()
      c.layer.remove()
      const s = scrollerRef.current
      if (s) {
        s.style.transform = ''
        s.style.willChange = ''
        s.style.scrollSnapType = ''
      }
      const root = rootRef.current
      if (root) root.style.overflow = ''
    }

    // Only stops the frames: runGlide calls this to supersede a previous glide, and the caller has already installed
    // the new snapshot by then.
    const cancelGlide = () => {
      if (glideAnimRef.current) {
        cancelAnimationFrame(glideAnimRef.current)
        glideAnimRef.current = 0
      }
    }

    // Each write fires a scroll event, so the frame path keeps doing its usual work and the observer's settle still
    // lands in onSettle — nothing downstream has to know who moved the scroll.
    const runGlide = (s: HTMLDivElement, from: number, to: number) => {
      cancelGlide()
      const started = performance.now()
      const last = GLIDE_SAMPLES.length - 1
      const step = () => {
        const t = Math.min(1, (performance.now() - started) / GLIDE_MS)
        const p = GLIDE_SAMPLES[Math.round(t * last)] ?? 1
        s.scrollLeft = from + (to - from) * p
        if (t < 1) {
          glideAnimRef.current = requestAnimationFrame(step)
          return
        }
        glideAnimRef.current = 0
        s.scrollLeft = to
        lastPositioned.current = Math.round(to / (widthRef.current || 1))
        holdProgrammatic()
      }
      holdProgrammatic()
      glideAnimRef.current = requestAnimationFrame(step)
    }

    // Only if the running slide is ours: the chrome stores are global and surfaces nest.
    const clearChromeIfOwn = () => {
      const running = transitionProgress.get()
      const g: readonly string[] = input.current.group
      if (!running || g.includes(running.from) || g.includes(running.to)) {
        navVisualPath.set(null)
        transitionProgress.clear()
      }
    }

    // Not before the destination has COMMITTED: navVisualPath is an override, and on a route whose loader outlasts
    // the slide the rendered match is still the ORIGIN — so releasing on the glide's last frame snaps the highlight
    // back. The only thing that may move it is the slide itself.
    const releaseChrome = (to: string) => {
      if (matchesFrom(pathnameRef.current, to)) {
        clearChromeIfOwn()
        return
      }
      pendingChromeRef.current = to
      if (pendingChromeTimer.current) clearTimeout(pendingChromeTimer.current)
      pendingChromeTimer.current = window.setTimeout(() => {
        pendingChromeTimer.current = 0
        if (pendingChromeRef.current !== to) return
        pendingChromeRef.current = null
        clearChromeIfOwn()
      }, PARK_WATCHDOG_MS)
    }

    // Two transform animations instead of a scroll: Element.animate hands both to the compositor, so the React commit
    // that mounts the destination mid-slide cannot stutter them.
    // Travel is ONE page for ANY distance — the slide is a fixed 100% translate and the scroll jump under it is free.
    // The chrome cannot be read off scrollLeft any more, so a rAF reads the animation's OWN clock; starve it and the
    // highlight lags, the motion does not.
    const runComposited = (args: {
      s: HTMLDivElement
      root: HTMLDivElement
      live: HTMLElement
      from: string
      to: string
      dir: 1 | -1
      target: number
    }) => {
      const { s, root, live, from, to, dir, target } = args
      endComposite()
      const { layer, applyScroll } = captureSnapshot(live)
      root.appendChild(layer)
      applyScroll()
      // A transformed child can still contribute scrollable overflow to <main>.
      root.style.overflow = 'hidden'
      // Snap off first: mandatory snap would fight a scrollLeft write landing between the slots it wants.
      s.style.scrollSnapType = 'none'
      s.scrollLeft = target
      holdProgrammatic()
      // The samples ARE the keyframes, evenly spaced, so `linear` between them replays the spring exactly.
      const opts: KeyframeAnimationOptions = { duration: GLIDE_MS, easing: 'linear', fill: 'both' }
      s.style.willChange = 'transform'
      const lead = s.animate(
        GLIDE_SAMPLES.map((p) => ({ transform: `translateX(${(dir * (1 - p) * 100).toFixed(3)}%)` })),
        opts,
      )
      const trail = layer.animate(
        GLIDE_SAMPLES.map((p) => ({ transform: `translateX(${(-dir * p * 100).toFixed(3)}%)` })),
        opts,
      )
      const last = GLIDE_SAMPLES.length - 1
      const readChrome = () => {
        const c = compositeRef.current
        if (!c) return
        const elapsed = Number(lead.currentTime ?? 0)
        const p = GLIDE_SAMPLES[Math.min(last, Math.max(0, Math.round((elapsed / GLIDE_MS) * last)))] ?? 1
        // Only the marker's progress: `to` was made active at click time and must not flip back.
        transitionProgress.set(from, to, Math.min(1, Math.max(0, p)))
        c.chromeRaf = requestAnimationFrame(readChrome)
      }
      compositeRef.current = { layer, anims: [lead, trail], chromeRaf: requestAnimationFrame(readChrome) }
      void lead.finished
        .then(() => {
          // Only OUR glide gets to finish the job — a superseding click already replaced compositeRef.
          if (compositeRef.current?.anims[0] !== lead) return
          lastPositioned.current = Math.round(target / (widthRef.current || 1))
          endComposite()
          glideRef.current = null
          holdProgrammatic()
          releaseChrome(to)
          // The positioning effect's own trim was unreachable for this navigation — see trimPreviews.
          trimPreviews(to)
        })
        .catch(() => {
          /* cancelled by a superseding glide or a finger — that path already cleaned up */
        })
    }

    const cacheSlots = () => {
      const s = scrollerRef.current
      if (!s) return
      slotGeom.set(
        s,
        [...s.children]
          .filter((c): c is HTMLElement => c instanceof HTMLElement)
          .map((el) => ({
            el,
            seam: el.querySelector<HTMLElement>(':scope > [data-rt-seam]'),
            left: el.offsetLeft,
          })),
      )
    }

    // Straight onto the strip, which is why it is a real element: a pseudo-element's opacity can only be driven
    // through an inheriting custom property, invalidating that slot's whole route pane on every frame of every swipe.
    const applySeams = () => {
      const s = scrollerRef.current
      if (!s || reducedMotionRef.current) return
      const sl = s.scrollLeft
      const w = widthRef.current
      for (const { seam, left } of slotGeom.get(s) ?? []) {
        if (!seam) continue
        const j = Math.min(1, Math.max(0, (sl - left + w) / (2 * w)))
        const next = j > 0.52 && j < 0.98 ? '1' : '0'
        if (seam.style.opacity !== next) seam.style.opacity = next
      }
    }
    return {
      activate,
      trimPreviews,
      holdProgrammatic,
      releaseProgrammatic,
      endComposite,
      cancelGlide,
      runGlide,
      clearChromeIfOwn,
      runComposited,
      cacheSlots,
      applySeams,
    }
  })

  // Covers the case the positioning effect cannot: our programmatic claim was still up when the commit landed.
  // Must stay BELOW the engine: the React Compiler requires it be declared before it is read in an effect.
  useEffect(() => {
    const pending = pendingChromeRef.current
    if (!pending || !matchesFrom(pathname, pending)) return
    pendingChromeRef.current = null
    if (pendingChromeTimer.current) {
      clearTimeout(pendingChromeTimer.current)
      pendingChromeTimer.current = 0
    }
    engine.clearChromeIfOwn()
    // The slide finished before this destination resolved, so the glide's own finish could not trim then.
    engine.trimPreviews(pending)
  }, [pathname, group, engine])

  // Returns false to be asked again — the observer only re-checks on its debounce path, since a scroll that has
  // already ended fires no second scrollend. The held-finger case never reaches here.
  const onSettle = (): boolean => {
    const s = scrollerRef.current
    if (!s) return true
    // A glide runs snapless, so mandatory snap is law again, and the rendered match is the destination now.
    s.style.scrollSnapType = ''
    glideRef.current = null
    const wasProgrammatic = programmaticRef.current
    if (wasProgrammatic) {
      // Hand it back here rather than on a clock: this settle IS the event the claim was waiting for, and a swipe that
      // still owes its navigation may be behind it.
      engine.releaseProgrammatic()
    }
    // FRESH width, never the cached one: a nested pager (the report tabs inside a stacked screen) is not viewport-wide.
    const w = s.clientWidth || widthRef.current || 1
    // Every refusal, including the passenger guard, is in scroll-pager-machine.ts and replayed as data there.
    const decision = resolveScrollCommit({
      currentIndex,
      group,
      pathname,
      scrollLeft: s.scrollLeft,
      width: w,
      userIntent: userIntentRef.current,
      programmatic: wasProgrammatic,
    })
    if (decision.kind === 'idle') return true
    if (decision.kind === 'retry') return false
    if (decision.kind === 'abandon') {
      // Whatever the user started ended without committing — a tap, a vertical scroll, a swipe that snapped back.
      userIntentRef.current = false
      s.removeAttribute('data-rt-user')
      if (!parkedFor) engine.clearChromeIfOwn() // never clear the held chrome mid-park
      return true
    }
    // A second swipe during a park re-commits to the newly landed member, so scroll-snap keeps the position.
    const { member, landed } = decision
    userIntentRef.current = false
    rtlog('scroll-commit', { scope, to: member })
    // Land EXACTLY on the slot first: the park freezes the position as-is, and a snap whose tail was interrupted can
    // rest a few px short — parked short it STAYS short for the whole park.
    if (Math.abs(s.scrollLeft - landed * w) >= 1) {
      engine.holdProgrammatic()
      s.scrollLeft = landed * w
    }
    // Reveal gate: keep the destination's already-loaded preview on screen and park the live <Outlet/> off-stage.
    engine.activate(member)
    navVisualPath.set(member)
    transitionProgress.set(member, member, 1)
    // The ref too, not just the state: router.navigate fires onBeforeLoad synchronously, before the state has flushed.
    parkedForRef.current = member
    setParkedFor(member)
    void router.navigate({ to: member })
    return true
  }

  /** One frame of a scroll, whoever owns it — the observer coalesces these to one per animation frame. */
  const onFrame = () => {
    const s = scrollerRef.current
    if (!s) return
    engine.applySeams()
    const w = widthRef.current
    const pos = s.scrollLeft / w
    const lo = Math.max(0, Math.floor(pos))
    const hi = Math.min(group.length - 1, Math.ceil(pos))
    const from = group[lo]
    const to = group[hi]
    // Only under a user-owned scroll: a post-settle bounce must not mount content into a slot merely corrected through.
    if (!programmaticRef.current) {
      if (from) engine.activate(from)
      if (to) engine.activate(to)
    }
    // A composited glide has already parked scrollLeft on the destination, so position tells us nothing here.
    if (compositeRef.current) return
    // A scroll-driven glide owns the chrome end-to-end, so the sections it scrolls past are never lit. Position-derived
    // members are only right for a finger, which really does travel through them.
    const glide = glideRef.current
    if (glide) {
      const span = glide.end - glide.start
      const p = span === 0 ? 1 : Math.min(1, Math.max(0, (s.scrollLeft - glide.start) / span))
      transitionProgress.set(glide.from, glide.to, p)
      navVisualPath.set(glide.to)
      return
    }
    // Drive the shared chrome: the underline rides the continuous position, the active-link flips at midpoint.
    const frac = pos - lo
    if (from && to && from !== to && frac > 0.001) {
      transitionProgress.set(from, to, frac)
      navVisualPath.set(frac >= 0.5 ? to : from)
      // Only for a scroll the USER owns: a page must never buzz when nobody is touching it. `lo` names the seam, so a
      // fling on through the next boundary ticks on ITS midpoint.
      if (userIntentRef.current && !programmaticRef.current) midHaptic(`${scope}:${lo}`, frac)
    } else {
      // A new member a swipe is about to commit to HOLDS the chrome: the commit lands a beat later, and clearing now
      // would flash the still-current OLD route as active.
      const landed = group[lo]
      const willCommit = !!landed && !matchesFrom(pathname, landed) && userIntentRef.current && !programmaticRef.current
      if (willCommit && landed) {
        transitionProgress.set(landed, landed, 1)
        navVisualPath.set(landed)
      } else if (!programmaticRef.current && !parkedFor) {
        engine.clearChromeIfOwn()
      }
    }
  }
  // Mirrored for the observer, which is installed once and cannot capture a re-created closure.
  const onFrameRef = useRef(onFrame)
  const onSettleRef = useRef(onSettle)
  // Layout effect, not render — see the first mirror above, and README Appendix C.
  useLayoutEffect(() => {
    onFrameRef.current = onFrame
    onSettleRef.current = onSettle
  })

  // ARM the glide BEFORE the router commits. Chrome tracks the SNAPPED ELEMENT and re-snaps to follow it, so a nav
  // that relocates the live slot slides scrollLeft onto the destination synchronously — and the positioning effect
  // below would find nothing left to move. Dropping snap inside that effect cannot help; layout effects run after
  // the mutation that already re-snapped.
  // onBeforeLoad, not onBeforeNavigate: it fires for push AND pop and survives redirects.
  useEffect(() => {
    if (inPreview || !expanded) return
    return router.subscribe('onBeforeLoad', (e) => {
      const from = e.fromLocation?.pathname
      const to = e.toLocation?.pathname
      if (!from || !to) return
      const s = scrollerRef.current
      if (!s) return
      const i = indexOfMember(group, from)
      const j = indexOfMember(group, to)
      const fromMember = group[i]
      const toMember = group[j]
      if (i < 0 || j < 0 || i === j || !fromMember || !toMember) return
      const w = s.clientWidth || 1
      // A swipe's own commit, not a click: the gesture owns the position, and the park/reveal depends on mandatory snap
      // holding it. parkedForRef is set by onSettle before the navigate precisely so this hook can tell the two apart.
      // A HELD FINGER, never userIntentRef: intent is stamped by any touch reaching this scroller, so it says nothing
      // about who owns the position now.
      // The position test stays TIGHT — anything wider swallows a genuine link nav that starts near its target.
      if (gestureRef.current?.held() || parkedForRef.current) return
      if (Math.abs(s.scrollLeft - j * w) < 4) return
      // This nav supersedes whatever the last one was still holding the chrome for; the claim below replaces it.
      pendingChromeRef.current = null
      widthRef.current = w
      s.style.scrollSnapType = 'none'
      // START THE SLIDE ON THIS FRAME, never on the router's commit — that wait is hundreds of milliseconds on a phone
      // where a tap does nothing. Nothing here needs the real route: the destination's slot mounts a RoutePreview.
      // Only the DESTINATION end is activated: the composited path freezes the outgoing pane as a snapshot layer, so it
      // needs no preview of its own. The scroll fallback below has no snapshot and does.
      const root = rootRef.current
      const liveEl = liveRef.current
      engine.activate(toMember)
      // Light the DESTINATION immediately — a tapped link must look pressed on the frame you tap it. The MARKER still
      // travels, animated from the glide's clock. A finger keeps the position-derived midpoint flip instead.
      transitionProgress.set(fromMember, toMember, 0)
      navVisualPath.set(toMember)
      const target = j * w
      // Marks the glide as ours, so the frame path keeps its hands off the chrome while the animation clock owns it.
      glideRef.current = { from: fromMember, to: toMember, start: s.scrollLeft, end: target }
      if (root && liveEl) {
        engine.runComposited({
          s,
          root,
          live: liveEl,
          from: fromMember,
          to: toMember,
          dir: j > i ? 1 : -1,
          target,
        })
        return
      }
      // No surface element to work with: fall back to scrolling. The ORIGIN needs its own preview on this path, since
      // nothing freezes it and the live outlet relocates away the moment the router commits.
      engine.activate(fromMember)
      engine.runGlide(s, s.scrollLeft, target)
    })
  }, [router, group, expanded, inPreview, engine])

  // Instant on the first layout and for a swipe-commit; a glide for a Link or back-forward landing on another member.
  useLayoutEffect(() => {
    const s = scrollerRef.current
    if (!s || inPreview || !expanded) return
    const w = s.clientWidth || 1
    widthRef.current = w
    // While parked, the live outlet is off-stage and the destination's preview is on screen: do not reposition.
    if (parkedFor) {
      engine.cacheSlots()
      engine.applySeams()
      return
    }
    // A glide of ours started at onBeforeLoad is already aiming at the right slot, and this effect re-runs ~50ms into
    // it — repositioning would restart the animation from wherever the pane had got to.
    if (glideAnimRef.current || compositeRef.current) {
      engine.cacheSlots()
      engine.applySeams()
      lastPositioned.current = currentIndex
      return
    }
    // A GESTURE OWNS THE POSITION — never reposition under it. This effect re-runs mid-gesture, and the write path
    // would read the finger's mid-travel position as "off the committed slot" and glide the pane back against it.
    // userIntentRef covers the released-but-still-settling tail.
    if (gestureRef.current?.held() || userIntentRef.current) {
      engine.cacheSlots()
      engine.applySeams()
      return
    }
    if (currentIndex < 0) return
    const target = currentIndex * w
    const isInitial = lastPositioned.current < 0
    if (Math.abs(s.scrollLeft - target) >= 1) {
      rtlog('reposition', { scope, from: Math.round(s.scrollLeft), to: target, initial: isInitial })
      engine.holdProgrammatic()
      if (isInitial || prefersReducedMotion()) {
        // Instant: nothing animates, so mandatory snap can stay law.
        s.style.scrollSnapType = ''
        s.scrollLeft = target
      } else {
        // Snap off so the glide is not yanked to a stop at a seam; onSettle restores it.
        s.style.scrollSnapType = 'none'
        // ONE page of travel: scrolling the real distance would blur every section in between, and the further the jump
        // the longer it reads. A non-adjacent jump TELEPORTS next to the destination, snapless and before paint, then
        // glides that last slot-width.
        const fromIndex = Math.round(s.scrollLeft / w)
        // CLAIM THE CHROME at progress 0, never on the glide's first scroll event: the rendered match has already
        // committed to the destination, so any gap lets the highlight sit there and then snap back.
        const fromMember = group[fromIndex]
        const toMember = group[currentIndex]
        if (Math.abs(currentIndex - fromIndex) > 1) {
          s.scrollLeft = (currentIndex + (currentIndex > fromIndex ? -1 : 1)) * w
        }
        // The slot the glide slides OFF is empty by now — the live <Outlet/> relocated in the commit that ran this effect —
        // and this path has no snapshot to hide that behind, so without a preview a blank page would travel.
        const startMember = group[Math.round(s.scrollLeft / w)]
        if (startMember) engine.activate(startMember)
        // The journey as its two ENDS, so a jump from the first section to the last lights those two and nothing else.
        if (fromMember && toMember && fromMember !== toMember) {
          glideRef.current = { from: fromMember, to: toMember, start: s.scrollLeft, end: target }
          transitionProgress.set(fromMember, toMember, 0)
          // Destination active at once, same as the click path — this is not a gesture either.
          navVisualPath.set(toMember)
        }
        // Reached when a member change arrives with no link navigation behind it (a redirect, a programmatic navigate,
        // a back/forward the subscription skipped), so the glide starts here instead of on the click frame.
        engine.runGlide(s, s.scrollLeft, target)
      }
    } else {
      // Nothing to move. Snap is law again — never leave it off, or the pager stops snapping.
      s.style.scrollSnapType = ''
    }
    lastPositioned.current = currentIndex
    engine.cacheSlots()
    engine.applySeams()
    // Settled and we own the position again: drop the user stamp, trim the previews, clear the chrome.
    if (!gestureRef.current?.held() && !programmaticRef.current) {
      userIntentRef.current = false
      s.removeAttribute('data-rt-user')
      const member = group[currentIndex]
      if (member) engine.trimPreviews(member)
      engine.clearChromeIfOwn()
    }
    // Listed because the engine READS them through `input`, not because any closure identity changes.
  }, [pathname, currentIndex, expanded, inPreview, parkedFor, group, engine, scope])

  // The parked live outlet is rendering the destination IN VIEW behind the cover, so its lazy images are loading:
  // wait for them to decode (or time out) before dropping the cover, so there is nothing left to draw.
  useEffect(() => {
    if (!parkedFor) return
    let cancelled = false
    let raf = 0
    const reveal = () => {
      if (cancelled) return
      rtlog('reveal', { scope, member: parkedFor })
      raf = requestAnimationFrame(() => setParkedFor(null))
    }
    if (matchesFrom(pathname, parkedFor)) {
      const slot = liveRef.current
      const imgs = slot ? [...slot.querySelectorAll('img')].filter((im) => !im.complete) : []
      if (imgs.length === 0) {
        reveal()
      } else {
        // decode() rejects on a broken img; race a timeout so a slow image cannot stall the reveal.
        void Promise.race([
          Promise.all(imgs.map((im) => im.decode().catch(() => {}))),
          new Promise((r) => setTimeout(r, 600)),
        ]).then(reveal)
      }
    }
    // The watchdog waits for an IDLE ROUTER, and that condition is the whole design: a SLOW commit is not the case it
    // exists for. The parked outlet renders the ORIGIN until the router commits, so dropping the cover on a plain
    // clock would show the page you swiped away from inside the page you swiped to.
    // So the two are told apart by the router's own STATUS rather than by a longer timeout.
    if (!routerPending) {
      parkWatchdog.current = window.setTimeout(() => {
        if (!cancelled) setParkedFor(null)
      }, PARK_WATCHDOG_MS)
    }
    return () => {
      cancelled = true
      if (raf) cancelAnimationFrame(raf)
      if (parkWatchdog.current) {
        clearTimeout(parkWatchdog.current)
        parkWatchdog.current = 0
      }
    }
  }, [parkedFor, pathname, routerPending, scope])

  // Keep the cached width fresh and stay centred on the current slot across a resize (rotation, window drag).
  useEffect(() => {
    const s = scrollerRef.current
    if (!s || inPreview) return
    const measure = () => {
      const w = s.clientWidth || 1
      const wasCentred = currentIndex >= 0 && Math.abs(s.scrollLeft - currentIndex * widthRef.current) < 2
      widthRef.current = w
      if (wasCentred && currentIndex >= 0 && !parkedFor) {
        engine.holdProgrammatic()
        s.scrollLeft = currentIndex * w
      }
      engine.cacheSlots()
      engine.applySeams()
    }
    const observer = new ResizeObserver(measure)
    observer.observe(s)
    return () => observer.disconnect()
  }, [inPreview, currentIndex, parkedFor, engine])

  // Unmount-only: `engine` never changes identity, so this cleanup cannot fire mid-flight.
  useEffect(
    () => () => {
      engine.cancelGlide()
      engine.endComposite()
    },
    [engine],
  )

  // The observer owns everything about WHEN; the release belongs to native scroll-snap, whole — nothing here writes
  // scrollLeft, snap-type or a transform on the way down.
  // INTENT is ours, because the observer cannot know it: a commit only fires for a scroll the user actually started.
  useEffect(() => {
    const s = scrollerRef.current
    if (!s || inPreview) return
    const markIntent = () => {
      if (suppressedRef.current) return
      userIntentRef.current = true
      // A finger beats an in-flight link glide: it really is dragging through the members between.
      engine.cancelGlide()
      engine.endComposite()
      glideRef.current = null
      // Chrome a finished glide still held is the finger's now, or a later release would blank a claim it does not own.
      pendingChromeRef.current = null
      if (programmaticRef.current) {
        engine.releaseProgrammatic()
        rtlog('scroll-guard-released', { scope })
      }
      s.setAttribute('data-rt-user', '')
    }
    const gesture = observeSnapScroller(s, {
      onFrame: () => onFrameRef.current(),
      onSettle: () => onSettleRef.current(),
    })
    gestureRef.current = gesture
    // The same signal the observer watches for the held-finger rules, read for a different question.
    s.addEventListener('touchstart', markIntent, { passive: true })
    s.addEventListener('pointerdown', markIntent, { passive: true })
    s.addEventListener('wheel', markIntent, { passive: true })
    return () => {
      gesture.stop()
      gestureRef.current = null
      s.removeEventListener('touchstart', markIntent)
      s.removeEventListener('pointerdown', markIntent)
      s.removeEventListener('wheel', markIntent)
    }
  }, [inPreview, scope, engine])

  // window.__rtDump() — run it in the console when the pager wedges (see debug.ts).
  useEffect(() => {
    const el = rootRef.current
    if (!el || inPreview) return
    return rtInstallDump(el, scope)
  }, [inPreview, scope])

  // Unmount-only, so it must not re-run when a callback identity changes.
  useEffect(() => {
    return () => {
      if (programmaticRelease.current) clearTimeout(programmaticRelease.current)
      if (parkWatchdog.current) clearTimeout(parkWatchdog.current)
      if (pendingChromeTimer.current) clearTimeout(pendingChromeTimer.current)
      engine.clearChromeIfOwn()
    }
  }, [engine])

  // A STABLE key so React RELOCATES the <Outlet/> rather than remounting the route when it leaves the park.
  const liveInner = (
    <div className={cn('rt-slot-inner', props.contentClassName)}>
      <Outlet />
    </div>
  )

  // All hooks above have run, so this early return is hook-safe.
  if (inPreview) {
    return (
      <div className={cn('relative isolate flex flex-1 flex-col', props.className)}>
        <div className={cn('flex min-h-0 flex-1 flex-col', props.contentClassName)}>
          <Outlet />
        </div>
      </div>
    )
  }

  // Parked = IN VIEW but covered, so its lazy images actually load; the reveal then just drops the attribute and
  // this same element flows into the slot the preview held, with nothing to redraw. Absolute, so it adds no extent.
  const parkIndex = parkedFor ? Math.max(0, indexOfMember(group, parkedFor)) : 0
  // Has the router ARRIVED at the member we parked for? Then the page under the cover is the real destination.
  const parkArrived = parkedFor !== null && matchesFrom(pathname, parkedFor)
  const coverIsLive = props.interactiveIncoming === true && !parkArrived
  const currentSlot = (parked: boolean) => (
    <div
      key="current"
      ref={liveRef}
      data-rt-slot="current"
      {...(parked
        ? {
            'data-rt-parked': '',
            // Just the index — app.css turns it into an offset. A plain custom-property key, since an `as string` KEY cast
            // bails the React Compiler out.
            'style': { '--rt-park-i': parkIndex },
          }
        : {})}
      className="rt-scroll-slot"
      // EXACTLY ONE of the two stacked pages is reachable: while the cover is live this parked outlet still renders the
      // route you LEFT, full width and in the viewport, so Tab and a screen reader would otherwise walk the stale page.
      inert={parked && coverIsLive}
    >
      {liveInner}
      <SeamStrip />
    </div>
  )
  // A neighbour slot, keyed by MEMBER so React carries it across the live-outlet relocation.
  // The parked destination's slot is a COVER, and it answers taps differently either side of the router's commit:
  // after parkArrived the real <Outlet/> is directly behind it at the same pixels, so the tap falls through; before
  // it, on an interactiveIncoming surface, the cover takes the tap itself into the preview you can actually see —
  // falling through then would act on the route you LEFT.
  // Dead is better than wrong; live-and-forwarded, then interactive-and-real, is better than both.
  const slotFor = (member: RoutePath, i: number) => (
    <div
      key={member}
      data-rt-slot={i < currentIndex ? 'prev' : 'next'}
      data-rt-to={member}
      {...(member === parkedFor && parkArrived ? { 'data-rt-cover': '' } : {})}
      className="rt-scroll-slot"
      // Nothing can reach a slot a finger is travelling toward anyway — see the header. The landed cover is the exception.
      inert={!(coverIsLive && member === parkedFor)}
    >
      <div className={cn('rt-slot-inner', props.contentClassName)}>
        {activated.has(member) || member === parkedFor ? <RoutePreview to={member} previewFrom={scope} /> : null}
      </div>
      <SeamStrip />
    </div>
  )

  // One flat keyed array in every case, so key="current" is a stable sibling React relocates rather than remounts.
  let children: ReactNode[]
  if (!expanded) {
    children = [currentSlot(false)]
  } else if (parkedFor) {
    children = [...group.map((m, i) => slotFor(m, i)), currentSlot(true)]
  } else if (currentIndex >= 0) {
    children = group.map((m, i) => (i === currentIndex ? currentSlot(false) : slotFor(m, i)))
  } else {
    children = [currentSlot(false)]
  }

  return (
    <div
      ref={rootRef}
      data-rt-surface={scope}
      data-rt-engine="scroll"
      className={cn('relative isolate flex flex-1 flex-col', props.className)}
    >
      <div ref={scrollerRef} className="rt-scroller">
        {children}
      </div>
    </div>
  )
}
